import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import AddressSelector from "@/components/AddressSelector";
import AddressForm from "@/components/AddressForm";
import { ArrowLeft, MapPin, Lightning, CreditCard, Money, Check, House, Briefcase, PencilSimple, Plus } from "@phosphor-icons/react";

export default function Checkout() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { getStoreCart, subtotal, clear, pincode, selectedAddress, setSelectedAddress } = useCart();
  const [store, setStore] = useState(null);
  const [pincodeInfo, setPincodeInfo] = useState(null);
  const [payment, setPayment] = useState("cod");
  const [submitting, setSubmitting] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loadedAddrs, setLoadedAddrs] = useState(false);

  // Load store
  useEffect(() => {
    api.get(`/storefront/${slug}`).then((r) => setStore(r.data));
  }, [slug]);

  // If the store does NOT support online payment, snap selection back to COD.
  useEffect(() => {
    if (store && store.online_payment_available === false && payment !== "cod") {
      setPayment("cod");
    }
  }, [store, payment]);

  // Auto-load default address if no selectedAddress
  useEffect(() => {
    if (!user || selectedAddress || loadedAddrs) return;
    setLoadedAddrs(true);
    api.get("/customer/addresses").then((r) => {
      const def = (r.data || []).find((a) => a.is_default) || (r.data || [])[0];
      if (def) setSelectedAddress(def);
    }).catch(() => {});
  }, [user, selectedAddress, setSelectedAddress, loadedAddrs]);

  // Pincode check whenever selected address / pincode changes
  useEffect(() => {
    const pin = selectedAddress?.pincode || pincode;
    if (pin && pin.length === 6) {
      api.get(`/storefront/${slug}/pincode/${pin}`).then((r) => setPincodeInfo(r.data));
    } else {
      setPincodeInfo(null);
    }
  }, [slug, pincode, selectedAddress]);

  if (!user) {
    sessionStorage.setItem("qd_return_to", `/${slug}/checkout`);
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center qd-card p-8 max-w-md">
          <h2 className="font-heading font-black text-2xl mb-2">Login required</h2>
          <p className="text-gray-600 text-sm mb-4">Please login to place your order</p>
          <Link to="/login" className="qd-btn-green inline-block" data-testid="checkout-login-redirect">Login with mobile</Link>
        </div>
      </div>
    );
  }

  if (!store) return <div className="p-8">Loading…</div>;

  const cart = getStoreCart(slug);
  const sub = subtotal(slug);
  const settings = store.settings || {};
  const freeAbove = Number(settings.free_delivery_threshold || 0);
  const deliveryFee = Number(settings.self_delivery_charge || 0);
  const minOrder = Number(settings.min_order_value || 0);
  const serviceable = !!pincodeInfo?.serviceable;
  const availability = store.availability_status || { is_open: true };
  const storeClosed = !availability.is_open;
  const belowMin = minOrder > 0 && sub < minOrder;
  const charge = serviceable && !storeClosed
    ? (freeAbove > 0 && sub >= freeAbove ? 0 : deliveryFee)
    : 0;
  const total = sub + charge;

  const placeOrder = async () => {
    if (!selectedAddress) return toast.error("Please select a delivery address");
    if (!serviceable) return toast.error(`This store does not deliver to ${selectedAddress.pincode}`);
    if (storeClosed) return toast.error(availability.reason || "Store is currently closed");
    if (belowMin) return toast.error(`Minimum order value is ₹${minOrder}. Add ₹${(minOrder - sub).toFixed(0)} more.`);
    setSubmitting(true);
    try {
      const r = await api.post("/orders", {
        store_id: store.id,
        items: cart.items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, qty: i.qty })),
        address: {
          name: selectedAddress.receiver_name,
          mobile: selectedAddress.receiver_mobile,
          address: selectedAddress.address + (selectedAddress.landmark ? `, ${selectedAddress.landmark}` : ""),
          pincode: selectedAddress.pincode,
          city: selectedAddress.city || "",
          landmark: selectedAddress.landmark || "",
        },
        payment_mode: payment,
        delivery_mode: "self",
      });
      clear(slug);
      toast.success("Order placed!");
      nav(`/${slug}/order/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
    }
    setSubmitting(false);
  };

  const Icon = selectedAddress?.label === "Work" ? Briefcase : selectedAddress?.label === "Other" ? MapPin : House;

  return (
    <div className="min-h-screen bg-[#F9F9F9] pb-32">
      <header className="sticky top-0 z-40 glass border-b border-gray-100 px-4 py-3 flex items-center gap-2">
        <Link to={`/${slug}/cart`} className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
          <ArrowLeft size={18} />
        </Link>
        <div className="font-heading font-black">Checkout</div>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* ETA / Serviceability */}
        <div className={`qd-card p-3 flex items-center gap-2 ${serviceable && !storeClosed ? "bg-[#F8CB46]/10" : "bg-amber-50"}`}>
          <Lightning weight="fill" size={18} className={serviceable && !storeClosed ? "text-[#15803D]" : "text-amber-700"} />
          <div className="text-sm font-bold" data-testid="checkout-eta-or-status">
            {storeClosed
              ? `Store closed · ${availability.reason || `Opens at ${availability.open_time || "09:00"}`}`
              : selectedAddress
                ? (serviceable ? `Delivery in ${pincodeInfo.eta_minutes} min` : `Not serviceable at ${selectedAddress.pincode}`)
                : "Choose a delivery address"}
          </div>
        </div>

        {/* Address card */}
        <div className="qd-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-base flex items-center gap-2">
              <MapPin weight="fill" size={18} className="text-[#15803D]" /> Delivery address
            </h3>
            {selectedAddress && (
              <button onClick={() => setShowSelector(true)} className="text-xs font-bold text-[#15803D] underline"
                data-testid="checkout-change-address">
                Change
              </button>
            )}
          </div>

          {selectedAddress ? (
            <div className="flex items-start gap-3" data-testid="checkout-selected-address">
              <div className="w-10 h-10 rounded-xl bg-[#F8CB46]/30 flex items-center justify-center flex-shrink-0">
                <Icon size={18} weight="fill" className="text-[#15803D]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-heading font-black text-sm">{selectedAddress.label}</span>
                  {selectedAddress.is_default && (
                    <span className="text-[9px] uppercase tracking-widest font-bold bg-[#15803D] text-white px-1.5 py-0.5 rounded-full">Default</span>
                  )}
                </div>
                <div className="text-xs text-gray-700">{selectedAddress.receiver_name} · {selectedAddress.receiver_mobile}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {selectedAddress.address}{selectedAddress.landmark ? `, ${selectedAddress.landmark}` : ""}, {selectedAddress.city ? `${selectedAddress.city}, ` : ""}<span className="font-mono">{selectedAddress.pincode}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setShowSelector(true)}
                className="qd-btn-green text-sm flex items-center gap-1" data-testid="checkout-select-address">
                <MapPin size={14} weight="fill" /> Select address
              </button>
              <button onClick={() => setShowForm(true)}
                className="qd-btn-primary text-sm flex items-center gap-1" data-testid="checkout-add-address">
                <Plus size={14} weight="bold" /> Add new
              </button>
            </div>
          )}
        </div>

        {/* Payment */}
        <div className="qd-card p-4 space-y-2">
          <h3 className="font-heading font-bold text-base mb-2">Payment method</h3>
          <PaymentOpt id="cod" current={payment} onSelect={setPayment} icon={Money} label="Cash on delivery" desc="Pay when you receive" />
          {store.online_payment_available ? (
            <PaymentOpt id="razorpay" current={payment} onSelect={setPayment} icon={CreditCard} label="Razorpay (UPI / Card)" desc="Mock — auto-success" />
          ) : (
            <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="checkout-online-payment-disabled">
              {store.online_payment_blocked_reason || "Online payment is currently unavailable. Please use Cash on delivery."}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="qd-card p-4 text-sm space-y-2">
          <h3 className="font-heading font-bold text-base mb-2">Order summary</h3>
          <div className="flex justify-between text-gray-600"><span>Items ({cart.items.length})</span><span>₹{sub}</span></div>
          <div className="flex justify-between text-gray-600">
            <span>Delivery</span>
            <span>
              {charge === 0
                ? (freeAbove > 0 && sub >= freeAbove ? "FREE" : (deliveryFee === 0 ? "FREE" : "—"))
                : `₹${charge}`}
            </span>
          </div>
          {freeAbove > 0 && sub < freeAbove && !storeClosed && serviceable && (
            <div className="text-[11px] text-[#15803D] font-bold" data-testid="checkout-free-delivery-hint">
              Add ₹{(freeAbove - sub).toFixed(0)} more for FREE delivery
            </div>
          )}
          {belowMin && (
            <div className="text-[11px] text-red-600 font-bold" data-testid="checkout-min-order-hint">
              Add ₹{(minOrder - sub).toFixed(0)} more · Minimum order ₹{minOrder}
            </div>
          )}
          <div className="flex justify-between font-heading font-black text-base pt-2 border-t"><span>Total</span><span>₹{total}</span></div>
        </div>
      </div>

      {/* Place order */}
      <div className="fixed bottom-0 inset-x-0 p-3 bg-white border-t border-gray-100">
        <button onClick={placeOrder} disabled={submitting || !selectedAddress || !serviceable || storeClosed || belowMin}
          className="w-full max-w-2xl mx-auto qd-btn-green flex items-center justify-between disabled:opacity-60"
          data-testid="checkout-place-order">
          <div className="text-left">
            <div className="text-xs opacity-90">
              {storeClosed ? "Store closed"
                : belowMin ? `Add ₹${(minOrder - sub).toFixed(0)} more`
                : `Pay ₹${total}`}
            </div>
          </div>
          <div>{submitting ? "Placing…"
            : storeClosed ? "Try later"
            : belowMin ? `Min ₹${minOrder}`
            : "Place order →"}</div>
        </button>
      </div>

      {showSelector && (
        <AddressSelector
          selectedId={selectedAddress?.id}
          onClose={() => setShowSelector(false)}
          onSelect={(a) => { setSelectedAddress(a); setShowSelector(false); }}
        />
      )}

      {showForm && (
        <AddressForm
          onClose={() => setShowForm(false)}
          onSaved={(a) => { setSelectedAddress(a); setShowForm(false); }}
          defaultPincode={pincode}
        />
      )}
    </div>
  );
}

function PaymentOpt({ id, current, onSelect, icon: Icon, label, desc }) {
  const active = current === id;
  return (
    <button onClick={() => onSelect(id)}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition ${active ? "border-[#15803D] bg-[#15803D]/5" : "border-gray-100"}`}
      data-testid={`payment-${id}`}>
      <Icon weight="duotone" size={24} className="text-[#15803D]" />
      <div className="flex-1 text-left">
        <div className="font-semibold text-sm">{label}</div>
        <div className="text-xs text-gray-500">{desc}</div>
      </div>
      {active && <Check weight="bold" size={20} className="text-[#15803D]" />}
    </button>
  );
}
